import { useAuth } from "@saleor/auth/AuthProvider";
import DeleteFilterTabDialog from "@saleor/components/DeleteFilterTabDialog";
import SaveFilterTabDialog, {
  SaveFilterTabDialogFormData
} from "@saleor/components/SaveFilterTabDialog";
import { DEFAULT_INITIAL_SEARCH_DATA } from "@saleor/config";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import useShop from "@saleor/hooks/useShop";
import { getStringOrPlaceholder, maybe } from "@saleor/misc";
import { useMallFilterData } from "@saleor/motherOrders/queries";
import useMallSearch from "@saleor/searches/useMallSearch";
import StoreOrderAssignRunnerDialog from "@saleor/storeOrders/components/StoreOrderAssignRunnerDialog";
import StoreOrderOverrideDialog from "@saleor/storeOrders/components/StoreOrderOverrideDialog";
import {
  handleUpdateStatusNotify,
  storeOrderMutationComplete
} from "@saleor/storeOrders/handlers";
import {
  useAssignRunnerMutation,
  useStoreOrderUpdateMutation
} from "@saleor/storeOrders/mutation";
import { ListViews } from "@saleor/types";
import {
  LockStatus,
  OrderDirection,
  SmopOrderStatusEnum,
  SmopStatusEnum,
  SmopUserTypeCode,
  StoreOrderSortField
} from "@saleor/types/globalTypes";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createFilterHandlers from "@saleor/utils/handlers/filterHandlers";
import createSortHandler from "@saleor/utils/handlers/sortHandler";
import {
  canManageAllMalls,
  canManageStoreOrders,
  canViewAllMalls,
  canViewMasterFulfillment,
  isUserWithMallAssignment
} from "@saleor/utils/handlers/userHandler";
import { getSortParams } from "@saleor/utils/sort";
import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";

import StoreOrderBarcodeDialog from "../../components/StoreOrderBarcodeDialog";
import StoreOrderConfirmDialog from "../../components/StoreOrderConfirmationDialog";
import StoreOrderListPage from "../../components/StoreOrderListPage/StoreOrderListPage";
import {
  useOrderDetailsQuery,
  useSearchRunner,
  useStoreOrderListQuery
} from "../../queries";
import {
  storeOrderListUrl,
  StoreOrderListUrlDialog,
  StoreOrderListUrlQueryParams,
  storeOrderUrl
} from "../../urls";
import {
  areFiltersApplied,
  deleteFilterTab,
  getActiveFilters,
  getFilterOpts,
  getFilterOptsWithMall,
  getFilterQueryParam,
  getFilterTabs,
  getFilterVariables,
  saveFilterTab
} from "./filters";
import { getDeliveryType, isParcelValidated } from "./handlers";
import { getSortQueryVariables } from "./sort";

interface StoreOrderListProps {
  params: StoreOrderListUrlQueryParams;
}

export const StoreOrderList: React.FC<StoreOrderListProps> = ({ params }) => {
  const navigate = useNavigator();
  const paginate = usePaginator();
  const shop = useShop();
  const location = useLocation();
  const {
    user: { userType }
  } = useAuth();
  const notify = useNotifier();
  const [hasPicking, setHasPicking] = React.useState(false);
  const [hasOutOfStock, setHasOutOfStock] = React.useState(false);
  const [orderMallId, setOrderMallId] = React.useState("");
  const { updateListSettings, settings } = useListSettings(
    ListViews.ORDER_LIST
  );
  const enableViewMasterFulfillment = canViewMasterFulfillment();
  const isOpsAdminView =
    userType.code === SmopUserTypeCode.OMI_OPS || enableViewMasterFulfillment;

  const createDefaultTabs = [
    {
      data: {
        status: SmopOrderStatusEnum.NEW_ORDER
      },
      name: "New Order"
    },
    {
      data: {
        status: SmopOrderStatusEnum.ACKNOWLEDGED
      },
      name: "Acknowledged"
    },
    {
      data: {
        status: SmopOrderStatusEnum.READY
      },
      name: "Ready"
    },
    {
      data: {
        status: SmopOrderStatusEnum.COLLECTED
      },
      name: "Collected"
    },
    {
      data: {
        status: SmopOrderStatusEnum.RECEIVED_AT_FC
      },
      name: "Received at FC"
    },
    {
      data: {
        status: SmopOrderStatusEnum.RETURN_TO_FC
      },
      name: "Returned to FC"
    },
    ...getFilterTabs()
  ];

  const tabs = createDefaultTabs;
  const DEFAULT_TAB_COUNT = 6;

  const getTabIndex = (
    { query, status }: StoreOrderListUrlQueryParams,
    tabs: any[]
  ) => {
    // If there are no custom search queries, set the currentTab according to the index of the status in createDefaultTabs array
    if (query === "" || query === undefined) {
      return tabs.findIndex(t => t.data.status === status);
    }

    // If there is a search term or any filter, current tab is very last index plus 1 (added custom filter tab)
    return tabs.length + 1;
  };

  const currentTab = isOpsAdminView
    ? params.activeTab === undefined
      ? areFiltersApplied(params)
        ? tabs.length + 1
        : 0
      : parseInt(params.activeTab, 0)
    : params.activeTab === undefined
    ? areFiltersApplied(params)
      ? getTabIndex(params, tabs)
      : tabs.length // Else, current tab is "All" tab
    : parseInt(params.activeTab, 0);

  const [changeFilters, resetFilters] = createFilterHandlers({
    createUrl: storeOrderListUrl,
    getFilterQueryParam,
    navigate,
    params
  });

  const [openModal, closeModal] = createDialogActionHandlers<
    StoreOrderListUrlDialog,
    StoreOrderListUrlQueryParams
  >(navigate, storeOrderListUrl, params);

  const handleTabChange = (tab: number) => {
    let urlParams = null;
    if (isOpsAdminView) {
      urlParams = {
        ...createDefaultTabs[tab - 1].data,
        activeTab: tab.toString()
      };
    } else {
      urlParams = {
        ...createDefaultTabs[tab].data,
        activeTab: tab.toString()
      };
    }

    navigate(storeOrderListUrl(urlParams));
  };

  const handleFilterTabDelete = () => {
    const currentCustomFilterTabIndex = currentTab - DEFAULT_TAB_COUNT;
    deleteFilterTab(currentCustomFilterTabIndex + 1); // +1 is to offset array index (workaround for splicing of orderFilter item in localStorage)
    navigate(storeOrderListUrl());
  };

  const handleFilterTabSave = (data: SaveFilterTabDialogFormData) => {
    const searchQuery = getActiveFilters(params).query || "";
    saveFilterTab(data.name, getActiveFilters(params));
    navigate(
      storeOrderListUrl({
        activeTab: tabs.length.toString(),
        query: searchQuery
      })
    );
  };
  useEffect(() => {
    if (params.payment) {
      params.after = null;
      params.before = null;
    }
  }, [params]);

  const paginationState = createPaginationState(settings.rowNumber, params);
  const currencySymbol = maybe(() => shop.defaultCurrency, "USD");

  const queryVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: getFilterVariables(params)
    }),
    [params, settings.rowNumber]
  );

  const checkParamsSort = () => {
    if (params.sort === "date" || params.sort === "number") {
      return getSortQueryVariables(params);
    } else {
      return {
        direction: OrderDirection.ASC,
        field: StoreOrderSortField.CREATION_DATE
      };
    }
  };

  const { data, loading, refetch } = useStoreOrderListQuery({
    displayLoader: true,
    variables: {
      ...queryVariables,
      filter: getFilterVariables(params),
      sort: checkParamsSort()
    }
  });

  const { data: storeOrder } = useOrderDetailsQuery({
    displayLoader: true,
    skip: params.orderId ? false : true,
    variables: {
      id: params.orderId
    }
  });

  const storeOrderLines =
    storeOrder?.storeOrder && storeOrder?.storeOrder.lines;

  React.useEffect(() => {
    if (storeOrderLines) {
      const indexNum = storeOrderLines.length - 1;

      const allStoreStatus = [];

      for (let o = 0; o <= indexNum; o++) {
        allStoreStatus.push(storeOrderLines[o].status);
      }

      if (allStoreStatus.includes("OUT_OF_STOCK")) {
        setHasOutOfStock(true);
      } else {
        setHasOutOfStock(false);
      }

      if (allStoreStatus.includes("PICKING")) {
        setHasPicking(true);
      } else {
        setHasPicking(false);
      }
    }
  }, [storeOrder]);

  const [updateOrderStatus] = useStoreOrderUpdateMutation({
    onCompleted: data => {
      handleUpdateStatusNotify(
        data,
        params.orderStatus,
        null,
        refetch,
        storeOrderListUrl,
        null,
        message => notify(message)
      );
    },
    onError: errors => {
      const [error] = errors.graphQLErrors;
      notify({
        status: "error",
        text: error?.message
      });
    }
  });

  const { search, loadMore, result } = useSearchRunner({
    skip: !orderMallId,
    variables: {
      first: 100,
      malls: [orderMallId],
      query: "",
      status: LockStatus.ACTIVE,
      userType: [SmopUserTypeCode.RUNNER]
    }
  });

  const [assignRunner] = useAssignRunnerMutation({
    onCompleted: data =>
      storeOrderMutationComplete(
        "ASSIGN_RUNNER",
        data,
        message => notify(message),
        null,
        storeOrderUrl,
        params.orderId,
        refetch
      ),
    onError: errors => {
      const [error] = errors.graphQLErrors;
      notify({
        status: "error",
        text: error?.message
      });
    }
  });

  const defaultToNewOrders = ({
    pathname,
    search
  }: {
    pathname: string;
    search: string;
  }): void => {
    // Checks if clicked tab is store orders tab from drawer or typed route in address bar is "/store-orders"
    if (pathname === "/store-orders" && search === "" && !isOpsAdminView) {
      navigate(
        storeOrderListUrl({
          activeTab: "0",
          status: "NEW_ORDER"
        })
      );
    }
  };

  useEffect(() => {
    defaultToNewOrders(location);
  }, [location]);

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    maybe(() => data.storeOrders.pageInfo),
    paginationState,
    params
  );
  const closeCamera = parcelID => {
    if (typeof parcelID === "string") {
      navigate(storeOrderListUrl({ ...params, action: null, query: parcelID }));
    } else {
      closeModal();
    }
  };

  const handleSearch = (text: string) => {
    navigate(storeOrderListUrl({ ...params, query: text }));
  };

  const handleSort = createSortHandler(navigate, storeOrderListUrl, params);

  const disableAction = !canManageStoreOrders();
  const disableMallActions = !canViewAllMalls() || isUserWithMallAssignment();
  const enableManageMallActions = canManageAllMalls();

  const { data: initialFilterData } = useMallFilterData({
    skip: disableMallActions,
    variables: {
      first: params?.mall && params.mall.length > 0 ? params.mall.length : 5,
      mall: params.mall
    }
  });

  const searchMall = useMallSearch({
    skip: disableMallActions,
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      filterStatus: SmopStatusEnum.ACT,
      first: 5
    }
  });

  const filterOpts = disableMallActions
    ? getFilterOpts(params)
    : getFilterOptsWithMall(params, {
        initial: initialFilterData?.malls?.edges?.map(edge => edge.node) || [],
        search: searchMall
      });

  return (
    <>
      <StoreOrderListPage
        brands={null}
        currencySymbol={currencySymbol}
        settings={settings}
        currentTab={currentTab}
        disabled={loading}
        filterOpts={filterOpts}
        orders={maybe(() => data.storeOrders.edges.map(edge => edge.node))}
        pageInfo={pageInfo}
        sort={getSortParams(params)}
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        onUpdateListSettings={updateListSettings}
        onRowClick={id => () => navigate(storeOrderUrl(id))}
        onSort={handleSort}
        onSearchChange={handleSearch}
        onFilterChange={changeFilters}
        onTabSave={() => openModal("save-search")}
        onTabDelete={() => openModal("delete-search")}
        openModal={() => openModal("barcode")}
        onTabChange={handleTabChange}
        initialSearch={params.query || ""}
        tabs={createDefaultTabs.map(tab => tab.name)}
        activeTab={params.activeTab}
        onAll={resetFilters}
        queryVariables={queryVariables}
        onOverrideStatus={(
          orderId,
          orderStatus: SmopOrderStatusEnum,
          newOrderStatus: SmopOrderStatusEnum
        ) =>
          openModal("onOverrideStatus", {
            newOrderStatus,
            orderId,
            orderStatus
          })
        }
        onUpdateStatus={(
          orderId,
          orderStatus: SmopOrderStatusEnum,
          newOrderStatus: SmopOrderStatusEnum
        ) =>
          openModal("onClickSaveButton", {
            newOrderStatus,
            orderId,
            orderStatus
          })
        }
        onAssignRunner={orderId =>
          openModal("onClickAssignRunner", {
            orderId
          })
        }
        onSubmitRunner={data => {
          assignRunner({
            variables: {
              runnerId: data.runnerId,
              storeOrderId: data.storeOrderId
            }
          });
        }}
        runnerList={maybe(() =>
          result.data.search.edges.map(edge => edge.node)
        )}
        fetchRunner={search}
        fetchMoreRunner={{
          hasMore: maybe(() => result.data.search.pageInfo.hasNextPage),
          loading: result.loading,
          onFetchMore: loadMore
        }}
        openGenerateModal={() => openModal("generateParcelID")}
        onAdd={() => undefined}
        disableAction={disableAction}
        disableMallActions={disableMallActions}
        enableManageMallActions={enableManageMallActions}
        isOpsAdminView={isOpsAdminView}
        setOrderMallId={setOrderMallId}
      />

      <SaveFilterTabDialog
        open={params.action === "save-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleFilterTabSave}
      />
      <DeleteFilterTabDialog
        open={params.action === "delete-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleFilterTabDelete}
        tabName={getStringOrPlaceholder(tabs[currentTab]?.name)}
      />
      <StoreOrderBarcodeDialog
        open={params.action === "barcode"}
        onClose={closeModal}
        handleClose={parcelID => closeCamera(parcelID)}
      />
      <StoreOrderOverrideDialog
        onClose={closeModal}
        open={params.action === "onOverrideStatus"}
        orderStatus={params.orderStatus}
        newOrderStatus={params.newOrderStatus}
        onSubmitOverride={overrideReason =>
          updateOrderStatus({
            variables: {
              id: params.orderId,
              overrideReason,
              smopStatus: params.newOrderStatus
            }
          })
        }
      />
      <StoreOrderConfirmDialog
        open={params.action === "onClickSaveButton"}
        onClose={closeModal}
        orderStatus={maybe(
          () => params.orderStatus,
          SmopOrderStatusEnum.NEW_ORDER
        )}
        handleSubmit={() =>
          updateOrderStatus({
            variables: { id: params.orderId, smopStatus: params.newOrderStatus }
          })
        }
        userType={maybe(() => userType.code, "")}
        isParcelIDValidated={isParcelValidated(storeOrder)}
        deliveryType={getDeliveryType(storeOrder)}
        hasPicking={hasPicking}
        hasOutOfStock={hasOutOfStock}
        storeOrderLines={storeOrderLines}
      ></StoreOrderConfirmDialog>
      <StoreOrderAssignRunnerDialog
        open={params.action === "onClickAssignRunner"}
        runner={storeOrder?.storeOrder?.runner}
        runnerList={maybe(() =>
          result.data.search.edges
            .map(edge => edge.node)
            .filter(suggestedProduct => suggestedProduct.id)
        )}
        onClose={closeModal}
        fetchRunner={search}
        fetchMoreRunner={{
          hasMore: maybe(() => result.data.search.pageInfo.hasNextPage),
          loading: result.loading,
          onFetchMore: loadMore
        }}
        loading={result.loading}
        onSubmit={runnerId =>
          assignRunner({
            variables: {
              runnerId,
              storeOrderId: params.orderId
            }
          })
        }
        orderMallId={params?.mall}
        setOrderMallId={setOrderMallId}
      ></StoreOrderAssignRunnerDialog>
    </>
  );
};

export default StoreOrderList;
